import React from 'react';
import { motion } from 'framer-motion';
import ProjectStatsBadge from '../../../components/ProjectStatsBadge';
import ShareProjectButton from '../../../components/ShareProjectButton/ShareProjectButton';
import './vista-holidays.css';
import docuVaultImg from '../../../assets/images/DocuVault Hero.png';

const DocuVault = () => {
  return (
    <motion.div 
      className="project-detail-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.h1 
        className="project-heading"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        DocuVault <span>Platform</span> – AI-Powered Secure Document Management Platform ☁️🔐
      </motion.h1>

      <motion.p 
        className="last-updated"
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      ><br />
        Last Updated On: 29 September 2026
      </motion.p>

      <div className="project-content">
        <motion.div 
          className="project-left"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <img src={docuVaultImg} alt="DocuVault Platform" className="project-image" />
          <h2 className="project-subtitle">DocuVault Platform</h2>
          <span className="project-category">Software Applications</span>
          <p className="project-author">Author: Praveen Kumar B</p>

          <ProjectStatsBadge docId="docuVault" trackView={true} />

          <div className="project-buttons">
            <a
              href="https://docu-vault-frontend.vercel.app/"
              className="visit-project-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              → Visit the Project
            </a>
            <a
              href="https://github.com/Praveenkumar-king"
              className="visit-project-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              → View on GitHub
            </a>
            <ShareProjectButton
              project={{
                id: "docuVault",
                title: "DocuVault",
                path: "/projects/projectdetail/docu-vault"
              }}
            />
          </div>
        </motion.div>

        <motion.div 
          className="project-right"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3 className="section-title">🔐 Overview</h3>
          <p className="overview-description">
            <strong>DocuVault</strong> is a secure, AI-powered digital document management and sharing platform built to help users store, organize, search, analyze, and securely share their documents from one centralized vault. ☁️📄
          </p>
          <p className="overview-description">
            It combines document management, AI intelligence, secure sharing, collaboration, analytics, automation, billing, and advanced security into a modern SaaS experience. 🚀
          </p>

          <h3 className="section-title">🚀 Key Features</h3>
          <ul className="feature-list">
            <li><strong>📂 Secure Document Management</strong> – Upload, organize, rename, favorite, archive, restore, and manage documents.</li>
            <li><strong>🗂️ Folders &amp; Tags</strong> – Organize documents using nested folders, categories, and tags.</li>
            <li><strong>🔎 Smart Search</strong> – Quickly find documents using keyword and intelligent search capabilities.</li>
            <li><strong>☁️ Cloud Storage</strong> – Secure document storage with Cloudinary and metadata management through MongoDB.</li>
            <li><strong>🔗 Secure Sharing</strong> – Share documents using controlled and protected share links.</li>
            <li><strong>🔐 Access Controls</strong> – Password protection, expiry dates, download limits, and access restrictions.</li>
            <li><strong>👥 Collaboration</strong> – Comments, mentions, threaded discussions, and document review workflows.</li>
            <li><strong>📊 Analytics Dashboard</strong> – Track documents, shares, downloads, campaigns, visitors, and usage.</li>
            <li><strong>🔔 Notifications</strong> – Real-time application notifications and email alerts for important activities.</li>
            <li><strong>⚙️ Automation</strong> – Event-driven notification and workflow automation system.</li>
          </ul>

          <h3 className="section-title">⚡ Advanced Features</h3>
          <ul className="feature-list">
            <li><strong>🤖 AI Document Intelligence</strong> – Extract information, summarize documents, identify important dates, and process document content.</li>
            <li><strong>🧠 AI Document Assistant</strong> – Ask questions about documents using RAG-powered contextual responses with source citations.</li>
            <li><strong>🔍 Semantic Search</strong> – Vector-based document retrieval using MongoDB Atlas Vector Search.</li>
            <li><strong>📑 Document Versioning</strong> – Maintain document versions and compare historical versions.</li>
            <li><strong>🧬 Document Similarity Detection</strong> – Detect exact duplicates and semantically related documents.</li>
            <li><strong>🛡️ Advanced Security Center</strong> – 2FA/TOTP, recovery codes, trusted devices, active sessions, login history, and security events.</li>
            <li><strong>💳 Subscription &amp; Billing</strong> – Free/Pro/Business-style plans, usage limits, Razorpay payments, subscriptions, and billing history.</li>
            <li><strong>🧾 Digital Invoices</strong> – Generate PDF invoices and deliver them through email after successful payments.</li>
            <li><strong>📈 Advanced Analytics &amp; AI Insights</strong> – Analyze real usage data with date-based metrics and insights.</li>
            <li><strong>👨‍💻 Developer Platform</strong> – API keys, scopes, rate limits, webhooks, integrations, API logs, and OpenAPI foundation.</li>
            <li><strong>👑 Admin Console</strong> – Manage users, documents, shares, billing, analytics, security, system settings, maintenance mode, and audits.</li>
            <li><strong>📧 Transactional Email System</strong> – Premium reusable email templates for security, documents, sharing, collaboration, billing, and system events.</li>
            <li><strong>🔄 Background Processing</strong> – Queue-based document processing with OCR, extraction, embeddings, and AI workflows.</li>
          </ul>

          <h3 className="section-title">💡 Why DocuVault?</h3>
          <p className="overview-description">
            DocuVault is designed beyond a basic file-upload application. 🔥
          </p>
          <p className="overview-description">
            It brings together:
          </p>
          <ul className="feature-list">
            <li>🔐 Security-first document management</li>
            <li>🤖 AI-powered document intelligence</li>
            <li>🔗 Controlled and secure sharing</li>
            <li>👥 Real-time collaboration &amp; reviews</li>
            <li>📊 Analytics &amp; automation</li>
            <li>💳 Production-ready SaaS billing</li>
            <li>🛡️ Enterprise-level account security</li>
            <li>👨‍💻 Developer &amp; integration capabilities</li>
          </ul>
          <p className="overview-description">
            The project demonstrates how a modern full-stack SaaS product can combine secure architecture, cloud storage, AI/RAG, analytics, payments, automation, and scalable backend services in a single platform. 🚀
          </p>

          <h3 className="section-title">🎯 Conclusion</h3>
          <p className="overview-description">
            <strong>DocuVault</strong> is a complete AI-powered secure digital document vault designed with real-world SaaS architecture in mind. ☁️🔐🤖
          </p>
          <p className="overview-description">
            From uploading and organizing documents to AI-powered search, secure sharing, collaboration, analytics, billing, automation, and security, DocuVault showcases my ability to design and develop a production-oriented full-stack application using modern technologies and scalable architecture. 💻🔥
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default DocuVault;
